import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { ChevronDown } from '@carbon/icons-react';
import cx from 'classnames';
import React, { useContext, useState } from 'react';
import SideNavIcon from './SideNavIcon.js';
import { Escape } from '../../internal/keyboard/keys.js';
import { match } from '../../internal/keyboard/match.js';
import { usePrefix } from '../../internal/usePrefix.js';
import { SideNavContext } from './SideNav.js';
var _ChevronDown;
var SideNavMenu = /*#__PURE__*/React.forwardRef(function SideNavMenu(_ref, ref) {
  var _cx;
  var customClassName = _ref.className,
    children = _ref.children,
    _ref$defaultExpanded = _ref.defaultExpanded,
    defaultExpanded = _ref$defaultExpanded === void 0 ? false : _ref$defaultExpanded,
    _ref$isActive = _ref.isActive,
    isActive = _ref$isActive === void 0 ? false : _ref$isActive,
    _ref$large = _ref.large,
    large = _ref$large === void 0 ? false : _ref$large,
    IconElement = _ref.renderIcon,
    isSideNavExpanded = _ref.isSideNavExpanded,
    tabIndex = _ref.tabIndex,
    title = _ref.title;
  var _useContext = useContext(SideNavContext),
    isRail = _useContext.isRail;
  var prefix = usePrefix();
  var _useState = useState(defaultExpanded),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var _useState3 = useState(defaultExpanded),
    _useState4 = _slicedToArray(_useState3, 2),
    prevExpanded = _useState4[0],
    setPrevExpanded = _useState4[1];
  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "--side-nav__item"), true), _defineProperty(_cx, "".concat(prefix, "--side-nav__item--active"), isActive || hasActiveDescendant(children) && !isExpanded), _defineProperty(_cx, "".concat(prefix, "--side-nav__item--icon"), IconElement), _defineProperty(_cx, "".concat(prefix, "--side-nav__item--large"), large), _defineProperty(_cx, customClassName, !!customClassName), _cx));
  if (!isSideNavExpanded && isExpanded && isRail) {
    setIsExpanded(false);
    setPrevExpanded(true);
  } else if (isSideNavExpanded && prevExpanded && isRail) {
    setIsExpanded(true);
    setPrevExpanded(false);
  }
  return (/*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    React.createElement("li", {
      className: className,
      onKeyDown: function onKeyDown(event) {
        if (match(event, Escape)) {
          setIsExpanded(false);
        }
      }
    }, /*#__PURE__*/React.createElement("button", {
      "aria-expanded": isExpanded,
      className: "".concat(prefix, "--side-nav__submenu"),
      onClick: function onClick() {
        setIsExpanded(!isExpanded);
      },
      ref: ref,
      type: "button",
      tabIndex: tabIndex === undefined ? !isSideNavExpanded && !isRail ? -1 : 0 : tabIndex
    }, IconElement && /*#__PURE__*/React.createElement(SideNavIcon, null, /*#__PURE__*/React.createElement(IconElement, null)), /*#__PURE__*/React.createElement("span", {
      className: "".concat(prefix, "--side-nav__submenu-title")
    }, title), /*#__PURE__*/React.createElement(SideNavIcon, {
      className: "".concat(prefix, "--side-nav__submenu-chevron"),
      small: true
    }, _ChevronDown || (_ChevronDown = /*#__PURE__*/React.createElement(ChevronDown, {
      size: 20
    })))), /*#__PURE__*/React.createElement("ul", {
      className: "".concat(prefix, "--side-nav__menu")
    }, children))
  );
});
SideNavMenu.displayName = 'SideNavMenu';
/**
Defining the children parameter with the type ReactNode | ReactNode[]. This allows for various possibilities:
a single element, an array of elements, or null or undefined.
**/
function hasActiveDescendant(children) {
  if (Array.isArray(children)) {
    return children.some(function (child) {
      if (! /*#__PURE__*/React.isValidElement(child)) {
        return false;
      }

      /** Explicitly defining the expected prop types (isActive and 'aria-current) for the children to ensure type
      safety when accessing their props.
      **/
      var props = child.props;
      if (props.isActive === true || props['aria-current'] || props.children instanceof Array && hasActiveDescendant(props.children)) {
        return true;
      }
      return false;
    });
  }

  // We use React.isValidElement(child) to check if the child is a valid React element before accessing its props

  if (/*#__PURE__*/React.isValidElement(children)) {
    var props = children.props;
    if (props.isActive === true || props['aria-current']) {
      return true;
    }
  }
  return false;
}
export { SideNavMenu, SideNavMenu as default };